import {
  attributes,
  AttributesMixin,
  define,
  Model,
  type
} from "@type-r/models";
import Flag from "src/constant/flag";
import {
  isFeatureEnabled,
  matchString,
  nestedIsValid,
  UploadSupportModel
} from "./common";
import { PartModel } from "./commonModels";
import { fetchJson, restfulIO } from "./endpoint";
import { localUserInfo } from "./localUserInfo";
import { OptionalOrgInfo, OrgInfo } from "./orgInfo";
import { ShippingNotice } from "./shipping";
import { Document } from "./uploads";
import { InvoiceModel } from "./invoice";
import { ParticipantRoles, OrderManagementPermission } from "./permissions";
import { globalStore } from "./store";
import { MultiSelectFilter } from "lqx-react";

export type Address = OrgInfo["address"];
export type ContactInfo = OrgInfo["contact"];

@define
export class OrderEvent extends attributes({
  event_id: Number,
  first_name: "",
  last_name: "",
  email: "",
  event: "",
  timestamp: Date
}) {
  get author() {
    return [
      this.first_name,
      this.last_name,
      this.email ? "<" + this.email + ">" : ""
    ]
      .join(" ")
      .trim();
  }
}

export interface OrderModel extends AttributesMixin<typeof OrderModel> {
  status: OrderStatus;
}

export const OrderStatusArray = [
  "PO Pending", 
  "Unvalidated", 
  "PO Complete", 
  "Partially Shipped", 
  "Shipped",
  "Partially Invoiced",
  "Invoiced",
  "Transferred",
  "Cancelled",
  "Error",
  "Expired"
] as const;

export type OrderStatus = typeof OrderStatusArray[number];

  export interface OrderFilters extends AttributesMixin<typeof OrderFilters> {}
  @define
  export class OrderFilters extends Model {
      static attributes = {
          filterAll : '',        
          orderStatuses: MultiSelectFilter        
      }
  
      initializeOptions() {
          this.filterAll = '';
          this.orderStatuses.set({selected: [], options: Array.from(new Set(OrderStatusArray.filter(x => x)))});
      }
  }

export const nutanixWorkFlowId = 1;
function nutanixRequired(x) {
  const isNutanix = this.getOwner().workflowMap.id === nutanixWorkFlowId;
  return !isNutanix || x;
}

nutanixRequired.error = "Required";

export interface OrderCount extends AttributesMixin<typeof OrderCount> {
  status: OrderStatus;
}

@define
export class OrderCount extends Model {
  static endpoint = restfulIO("/api/v2/po/count");

  public static getFeatureEnabledEndpoint(){
    return isFeatureEnabled("enableOmApi") ? restfulIO("/api/v3/po/count") : restfulIO("/api/v2/po/count");
  }

  public getEndpoint(){
    return isFeatureEnabled("enableOmApi") ? restfulIO("/api/v3/po/count") : restfulIO("/api/v2/po/count");
  }

  static attributes = {
    status: type(String).dontSave,
    count: Number
  };
  
  category_count(category: String) {
    switch (category) {
      case "pending":
        return this.status === "PO Pending" || this.status === "Unvalidated"
          ? this.count
          : 0;
      case "active":
        return this.status === "PO Complete" ||
          this.status === "Partially Shipped" ||
          this.status === "Shipped" ||
          this.status === "Partially Invoiced"
          ? this.count
          : 0;
      case "invoiced":
        return this.status === "Invoiced" ? this.count : 0;
      case "inactive":
        return this.status === "Transferred" ||
          this.status === "Cancelled" ||
          this.status === "Error"
          ? this.count
          : 0;
      case "expired":
        return this.status === "Expired" ? this.count : 0;
    }
  }
}

@define
export class OrderModel extends Model implements UploadSupportModel {
  static endpoint = restfulIO("/api/v2/po");

  public static getFeatureEnabledEndpoint(){
    return isFeatureEnabled("enableOmApi") ? restfulIO("/api/v3/po") : restfulIO("/api/v2/po");
  }

  public getEndpoint(){
    return isFeatureEnabled("enableOmApi") ? restfulIO("/api/v3/po") : restfulIO("/api/v2/po");
  }

  static attributes = {
    // allowEditsBySupplier: Boolean,

    //# list, get, and put
    po_number: String,

    quote: type({
      id: String,
      expiry: type(Date).null.dontSave,
      date: type(Date).null.dontSave,
      transaction_time: type(Date).null.dontSave,
      order_id: type(String).dontSave
    }),

    //# list, get (toJSON(false))
    buyer_name: String,
    flag_customer: String,
    flag_distributor: String,
    flags: String,
    last_updated: type(Date).null.dontSave,
    sales_order_id: String,
    status_flex: type(String).dontSave,
    status: type(String).dontSave,

    expiry_date : Date,
    supplier_id: String,

    //# LIST only fields missing in GET (mistake?)
    total_price: type(Number).dontSave,
    created_on_utc: type(Date).null.dontSave,
    // has_uploads: false

    //### GET ONLY
    po_details: type({
      po_date: type(Date).null,
      model: "",
      internal_id: "",
      end_customer_id: "",
      distributor_po_number: type(String).parse(x => x || ''),
    }).null,

    distributor: type({
      ...OptionalOrgInfo.attributes,
      id: String
    }).null.dontSave,
    distributor_po_number: String,

    ship_to: type(OrgInfo).null,
    bill_to: type(OptionalOrgInfo).null,
    end_customer: type(OptionalOrgInfo).null,

    shipping: type({
      account: String,
      carrier: type(String).required,
      instructions: String,
      method: type(String).required,
      notice_count: type(Number).dontSave,
      po_number: type(String).required,
    }).null,

    supplier: type({
      ...OptionalOrgInfo.attributes,
      currency: type(String).dontSave,
      terms: type(String).dontSave,
      inco_terms: type(String),
      vendor_id: type(String).dontSave
    }).null,

    unvalidated_reason: type(String).null.dontSave,

    comment: type(String).parse(x => x || ''),
    note: type(String).parse(x => x || ''),
    end_customer_po: type(String).parse(x => x || ''),

    conversation_id: Number,

    metadata: type({
      po_type: String,
      so_type: String,
      opportunity_id: String,
      ref_sales_order_id: type(String).parse(x => x || '')
    }).parse(x=>x || {}),

    logs: type([OrderEvent]).dontSave.endpoint(restfulIO("./logs")),

    // invoicesInfo: type([InvoiceModel]).dontSave.endpoint(restfulIO("./invoices")),

    parts: type([PartModel]).dontSave.endpoint(restfulIO("./parts"))
          .check(x => x && x.length, "At least one component is required")
          .check(nestedIsValid),

    shipment: type([ShippingNotice]).dontSave.endpoint(restfulIO("./shipment")),

    uploads: type([Document]).dontSave.endpoint(restfulIO("./uploads"))

  };

  // Synonim to make the sort work, until we refactor the sort to reverse the order in sortBy object to support extractor functions.
  get quote_id() {
    return this.quote.id;
  }

  get supplierName(): string {
    const relationship = globalStore.getRelationship(this.conversation_id);
    const supplier = relationship && relationship.Supplier;
    return !!supplier && (supplier.companyShortname || supplier.companyName);
  }

  get customerName(): string {
    const relationship = globalStore.getRelationship(this.conversation_id);
    const customer = relationship && relationship.Customer;
    return !!customer && (customer.companyShortname || customer.companyName);
  }

  get workflowMap() {
    const conversation = globalStore.conversations.get(
      this.conversation_id as any
    );

    if (conversation) {
      return conversation.workflowMap;
    }
  }

  get title(): string {
    const titleComponents = [this.customerName, this.supplierName].filter(
      x => x
    );
    const wfMap = this.workflowMap;
    if (wfMap && wfMap.description) {
      titleComponents.push(wfMap.description);
    }

    return titleComponents.join(" - ");
  }

  get organization() {
    return this.isSupplier || localUserInfo.isAdminRead
      ? this.distributor.name
      : this.supplier.name;
  }

  async fetch(options?: object) {
    await Promise.all([
      super.fetch(options),
      this.shipment.fetch(),
      this.parts.fetch(),
      // this.invoicesInfo.fetch(),
      this.logs.fetch(),
      this.uploads.fetch()
    ]);

    return this;
  }

  get isReadOnly() {
    return (
      this.status === "Shipped" ||
      this.status === "Partially Shipped" ||
      this.status === "Partially Invoiced" ||
      this.status === "Invoiced"
    );
  }

  get category(): OrderCategory {
    switch (this.status) {
      case "PO Pending":
      case "Unvalidated":
        return "pending";

      case "PO Complete":
      case "Partially Shipped":
      case "Shipped":
      case "Partially Invoiced":
        return "active";

      case "Invoiced":
        return "invoiced";

      case "Transferred":
      case "Cancelled":
      case "Error":
        return "inactive";

      case "Expired":
        return "expired";
    }
  }

  async doReset() {
    return fetchJson("POST", `/api/v2/po/${this.id}/reset`, {
      quote_id: this.quote.id
    });
  }

  get disableValidatePOButton() {
    //DISTI-2291: disable validate po button if
    // user is a supplier/super supplier
    // and order is unvalidated or po pending
    // and quote confirmed xml has not been sent (pdf purchase order not uploaded)
    // return !this.isValid() ||
    // ( isFeatureEnabled( 'NutanixXml' ) && this.isSupplier
    //   &&['Unvalidated', 'PO Pending'].includes(this.status)
    //   && !this.quoteConfirmedXmlSent )
    // DISTI-2439 reverses DISTI-2291
    return !this.isValid();
    //return false;
  }

  get quoteConfirmedXmlSent() {
    return this.logs.some(x => x.event_id == 53);
  }

  async doConfirmPO() {
    try {
      await fetchJson("POST", `/api/v2/po/${this.id}/confirm`, {});
    } finally {
      await Promise.all([this.logs.fetch()]);
    }
  }

  async doValidatePO({ commentOnSave = "", isValidation = true }) {
    const {
      id,
      po_number,
      quote,
      shipping,
      ship_to,
      bill_to,
      end_customer,
      comment,
      end_customer_po,
      po_details,
      metadata,
      supplier
    } = this.toJSON();

    const customer = getCustomerName(this.attributes.conversation_id);
    if(supplier.inco_terms.length > 3 && customer.toLowerCase() =='rubrik') {
        throw new Error('INCO Terms should be maximum 3 characters' );
    }
    return fetchJson("POST", `/api/v2/po/${this.id}/validate`, {
      po: {
        id,
        po_number,
        quote,
        shipping,
        ship_to,
        bill_to,
        end_customer,
        comment,
        end_customer_po,
        po_details,
        metadata,
        supplier
      },
      parts: this.parts.toJSON(),
      commentOnSave,
      isValidation
    });
  }

  shouldPublishNutanixXml(upload_type): boolean {
    //DISTI-2217,2291: only publish nutanix xml iff:
    // order status is unvalidated or po_pending
    // upload_type is purchase_order_pdf
    // user_type is supplier or a super supplier
    // confirmation xml has not been sent yet
    //DISTI-2439 removes the below restriction
    //['Unvalidated', 'PO Pending'].includes(this.status)
    //DISTI-2564 reverses DISTI-2439 (it applies for quote engaged messages only)
    return (
      upload_type === "purchase_order_pdf" &&
      this.isSupplier &&
      !this.quoteConfirmedXmlSent &&
      ["Unvalidated", "PO Pending"].includes(this.status) &&
      isFeatureEnabled("NutanixXml")
    );
  }

  async doUpload(file, comment, uploadType, documentType) {
    try {
      const [upload] = this.uploads.add({
        file_name: file.name,
        file_comments: comment,
        purchase_orders_id: this.id,
        upload_po_status: this.status,
        user_id: localUserInfo.id,
        publishNutanixXml: this.shouldPublishNutanixXml(uploadType)
      });

      await upload.save({
        params: { file }
      });
    } finally {
      await Promise.all([
        // NOTE: Nobody recalls this function. Temporarily comment it out
        // this.doNotifyPDFUpload(),
        setTimeout(() => {
          this.logs.fetch();
        }, 10000),
        this.uploads.fetch()
      ]);
    }
  }

  async doNotifyPDFUpload() {
    await fetchJson("POST", `/api/v2/po/${this.id}/notify_pdf_upload`, {
      id: this.id,
      status: this.status
    });
  }

  get isCustomerWrite(): boolean {
    return localUserInfo.hasSomePermissions(
      "OrderManagement",
      ParticipantRoles.Customer,
      [OrderManagementPermission.CustomerWrite],
      this.conversation_id
    );
  }

  get isSupplier(): boolean {
    return localUserInfo.hasSomePermissions(
      "OrderManagement",
      ParticipantRoles.Supplier,
      [
        OrderManagementPermission.SupplierRead,
        OrderManagementPermission.SupplierWrite,
        OrderManagementPermission.SupplierSuperSupplier
      ],
      this.conversation_id
    );
  }

  get isWriteSupplier(): boolean {
    return localUserInfo.hasSomePermissions(
      "OrderManagement",
      ParticipantRoles.Supplier,
      [
        OrderManagementPermission.SupplierWrite,
        OrderManagementPermission.SupplierSuperSupplier
      ],
      this.conversation_id
    );
  }

  get isSuperSupplier(): boolean {
    return localUserInfo.hasSomePermissions(
      "OrderManagement",
      ParticipantRoles.Supplier,
      [OrderManagementPermission.SupplierSuperSupplier],
      this.conversation_id
    );
  }

  get isDistributorHold() {
    return this.flag_customer === "hold";
  }

  get isCancelled() {
    return this.flag_customer === "Cancelled";
  }

  async doCancel() {
    return fetchJson("POST", `/api/v1/po/${this.id}/cancel/distributor`, {});
  }

  async doHold(hold: boolean) {
    let url = `/api/v1/po/${this.id}/hold/distributor`;

    if (hold == false) {
      url += "/remove";
    }

    return fetchJson("POST", url, {});
  }

  async doSubmitPO({ commentOnSave = "", isValidation = false }) {
    return this.doValidatePO({
      commentOnSave,
      isValidation
    });
  }

  get hasPdfUploads() {
    return this.uploads.some(x => x.isPdf());
  }

  get displayFlags() {
    let { status_flex, flag_customer } = this;

    status_flex = status_flex && status_flex.toLowerCase();
    flag_customer = flag_customer && flag_customer.toLowerCase();

    let supplierFlag;

    if (status_flex === Flag.cancelled) {
      supplierFlag = "Supplier Cancelled";
    } else {
      supplierFlag = status_flex === Flag.hold ? "Supplier Hold" : "";
    }
    let distributorFlag;
    if (flag_customer === Flag.cancelled) {
      distributorFlag = "Customer Cancelled";
    } else {
      distributorFlag = flag_customer === Flag.hold ? "Distributor Hold" : "";
    }

    if (status_flex && status_flex !== Flag.hold_released) {
      return `${supplierFlag}${
        distributorFlag ? " (" + distributorFlag + ")" : ""
      }`;
    }

    return `${supplierFlag}${
      distributorFlag ? " (" + distributorFlag + ")" : ""
    }`;
  }

  match(text: string) {
    return (
      !text ||
      text.length < 2 ||
      matchString(this.status, text) ||
      matchString(this.po_number, text) ||
      matchString(this.sales_order_id, text) ||
      matchString(this.quote.id, text) ||
      matchString(this.supplier.name, text) ||
      matchString(this.distributor.name, text)
    );
  }

  static categories: OrderCategory[] = [
    "pending",
    "active",
    "invoiced",
    "inactive",
    "expired"
  ];

  static sortBy = {
    created_on_utc: "Created On",
    last_updated: "Last Updated",
    status: "Status",
    quote_id: "Quote ID",
    sales_order_id: "Sales Order ID",
    po_number: "PO Number",
    total_price: "Total Price",
    transaction_time: "Transaction Time",
    buyer_name: "Buyer"
  };
}

export type Shipping = OrderModel["shipping"];

const allCategories = {
  pending: true,
  active: true,
  invoiced: true,
  inactive: true
};

export type OrderColor = "blue" | "green" | "grey" | "yellow" | "red";
export type OrderCategory =
  | "pending"
  | "active"
  | "invoiced"
  | "inactive"
  | "expired";

  export const getCustomerName = (conversation_id) => {
    const relationship = globalStore.getRelationship(conversation_id);
    const customer = relationship && relationship.Customer;
    const customerName =  !!customer && (customer.companyShortname || customer.companyName);
    return customerName ? customerName : '';
  }

  export const getSupplierName = (conversation_id) => {
    const relationship = globalStore.getRelationship(conversation_id);
    const supplier = relationship && relationship.Supplier;
    const supplierName = !!supplier && (supplier.companyShortname || supplier.companyName);
    return supplierName ? supplierName : '';
  }

